import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthContext } from "../context/context";
import { loginUser } from "../api";
import InputText from "../Components/Inputs/InputText";
import { useLanguage } from "../context/LanguageContext";
import Headline from "../Components/Elements/Headline";
import AuthWrapper from "../Components/Native/AuthWrapper";
import ReCAPTCHA from "react-google-recaptcha";
import { trackLogin, trackPageView } from "../utils/analytics";
import BtnPrimaryM from "../Components/Buttons/BtnPrimaryM";
import { useToast } from "../context/ToastContext";

const Login = () => {
  const navigate = useNavigate();
  const auth = useAuthContext();
  const { copy } = useLanguage();
  const location = useLocation();
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const { showToast } = useToast();

  const [recaptchaRequired, setRecaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loginSchema = z.object({
    email: z
      .string()
      .email(copy.login.errors.invalid_email)
      .min(1, copy.login.errors.email_required),
    password: z.string().min(1, copy.login.errors.password_required),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onSubmit",
  });

  useEffect(() => {
    // Track page view
    trackPageView("/login");

    const params = new URLSearchParams(location.search);
    const customMessage = params.get("message");
    const emailChanged = params.get("emailChanged");
    
    // Debug logging
    console.log("Login page URL params:", Object.fromEntries(params.entries()));
    console.log("Custom message:", customMessage);
    console.log("Email changed:", emailChanged);

    if (customMessage) {
      console.log("Setting custom message toast");
      showToast(decodeURIComponent(customMessage), "success");
    } else if (emailChanged === "true") {
      console.log("Setting email changed toast");
      showToast(copy.login.success.email_updated, "success");
    } else if (params.get("verified") === "true") {
      showToast(copy.login.success.email_verified, "success");
    } else if (params.get("passwordReset") === "true") {
      showToast(copy.login.success.password_reset, "success");
    } else if (params.get("passwordUpdated") === "true") {
      console.log(true);
      showToast(copy.profile.errors.password_updated, "success");
    } else if (params.get("passwordAdded") === "true") {
      showToast(copy.profile.errors.password_updated.replace(
          "updated",
          "added"
        ), "success");
    }
  }, [location, showToast]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    if (recaptchaRequired && !captchaToken) {
      showToast(copy.login.errors.captcha_required, "error");
      return;
    }

    try {
      const result = await loginUser({ ...data, captchaToken });

      // Track successful login
      trackLogin("email");

      // Reset reCAPTCHA status on successful login
      setRecaptchaRequired(false);
      setCaptchaToken(null);

      // CRITICAL: Store the auth token if provided
      if (result.token) {
        localStorage.setItem('authToken', result.token);
      }

      // Set the initial user data
      auth.setUserData(result.userData);
      auth.setAuthStatus("success");
      auth.setLoggedIn(true);

      // Fetch complete user data with all nested information (goals, finance, etc.)
      // This ensures YearProvider and other components have the data they need
      try {
        await auth.refetchUserData();
      } catch (refetchError) {
        console.error("Error refetching user data:", refetchError);
        // Continue anyway - we have basic userData
      }

      // Navigate after ensuring state is fully updated
      navigate("/");
    } catch (err) {
      console.error("Login failed:", err.message);
      // Check if the error response indicates reCAPTCHA is required
      if (
        err.response &&
        err.response.data &&
        err.response.data.recaptchaRequired
      ) {
        setRecaptchaRequired(true);
        setCaptchaToken(null); // Reset captcha token for new attempt
        showToast(
          err.response.data.message || copy.login.errors.captcha_verification,
          "error"
        );
      } else if (err.response && err.response.status === 429) {
        // Handle too many login attempts
        showToast(
          err.message ||
          err.response.data.message ||
          "Too many login attempts. Please try again later.",
          "error"
        );
      } else {
        // Use the error message from the API or fallback to generic message
        showToast(err.message || copy.login.errors.invalid_credentials, "error");
      }
    }
    setIsLoading(false);
  };

  const handleForgotPasswordClick = () => {
    navigate("/forgot-password");
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  return (
    <>
      {/* Prominent success banner for email change REMOVED. Only use global toast. */}
      <div className="w-full min-h-screen h-full flex flex-col justify-center items-center background-page-color gap-8">
        <a
          href="https://www.niumi.dk/"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-all duration-300 font-accent color-primary text-5xl"
        >
          NIUMI
        </a>

        <div className="card-base p-6 sm:p-8 card-base-border bg-white w-full max-w-[90vw] sm:w-[35vw] gap-4 sm:gap-6 flex flex-col">
          <Headline
            headline={copy.login.headline}
            description={copy.login.description}
          />

          <div className="flex flex-col gap-6">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-6"
            >
              <InputText
                label={copy.login.form.label_email}
                type="email"
                placeholder={copy.login.form.placeholder_email}
                {...register("email")}
                error={errors.email?.message}
                disabled={isLoading}
              />

              <InputText
                label={copy.login.form.label_password}
                type="password"
                placeholder={copy.login.form.placeholder_password}
                {...register("password")}
                error={errors.password?.message}
                disabled={isLoading}
              />

              {recaptchaRequired && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    sitekey={siteKey}
                    onChange={handleCaptchaChange}
                    disabled={isLoading}
                  />
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  className="text-sm text-primary hover:text-primary-dark disabled:opacity-50 cursor-pointer hover:underline"
                  disabled={isLoading}
                >
                  {copy.login.form.text_forgot_password}
                </button>
              </div>
              <div className="flex justify-center">
                <BtnPrimaryM
                  type="submit"
                  buttonCTA={
                    isLoading
                      ? copy.login.form.button_loading
                      : copy.login.form.button_submit
                  }
                  disabled={isLoading || (recaptchaRequired && !captchaToken)}
                />
              </div>
            </form>

            <div className="flex items-center gap-4 my-2">
              <div className="flex-1 h-px card-base-border-top"></div>
              <p className="text-sm">{copy.login.form.text_or}</p>
              <div className="flex-1 h-px card-base-border-top"></div>
            </div>

            <div className="w-auto flex justify-center">
              <AuthWrapper />
            </div>
          </div>

          <div className="text-center flex gap-1 text-sm self-center">
            <p>{copy.login.form.text_no_account}</p>
            <Link
              to="/register"
              className="text-primary hover:text-primary-dark hover:underline cursor-pointer"
            >
              {copy.login.form.text_register}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
